import React, { useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import { collection, getDocs } from "firebase/firestore";
import ModalDialogTitle from "../common/ModalDialogTitle";
import { createTpInstructorUser, db } from "../../config/firebase";
import { downloadExcelTemplate } from "../../utils/exportTemplate";
import { joinName } from "../../utils/name";

const DEFAULT_PASSWORD = "TpInst26!$?";

const TEMPLATE_FIELDS = ["Email", "First Name", "Last Name", "Phone"];

// Match a header regardless of spacing/underscores/case, e.g.
// "First Name" / "first_name" / "FirstName" all match.
const normalizeHeader = (header) =>
  String(header ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const initialResults = { successful: 0, failed: 0, errors: [] };

const InstructorBulkUpload = ({ onComplete }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [results, setResults] = useState(initialResults);
  const [parseError, setParseError] = useState("");
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setTotal(0);
    setProcessed(0);
    setResults(initialResults);
    setParseError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (uploading) return; // block close while a batch is in flight
    setOpen(false);
    if (results.successful > 0 && onComplete) onComplete();
  };

  const handleFileChange = (e) => {
    setParseError("");
    setFile(e.target.files[0] || null);
  };

  const handleDownloadTemplate = () => {
    downloadExcelTemplate(TEMPLATE_FIELDS, "instructors-template");
  };

  const handleUpload = () => {
    if (!file) return;
    setParseError("");
    setResults(initialResults);
    setUploading(true);

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const headers = rows[0] || [];
        const normalizedHeaders = headers.map(normalizeHeader);

        const emailIdx = normalizedHeaders.findIndex((h) =>
          h.includes("email"),
        );
        const firstNameIdx = normalizedHeaders.findIndex(
          (h) => h === "firstname",
        );
        const lastNameIdx = normalizedHeaders.findIndex(
          (h) => h === "lastname",
        );
        const phoneIdx = normalizedHeaders.findIndex((h) => h === "phone");

        if (emailIdx === -1 || firstNameIdx === -1 || lastNameIdx === -1) {
          setParseError(
            "The file must include 'Email', 'First Name', and 'Last Name' columns.",
          );
          setUploading(false);
          return;
        }

        const dataRows = rows
          .slice(1)
          .filter((row) => row[emailIdx] && String(row[emailIdx]).trim());

        setTotal(dataRows.length);
        setProcessed(0);

        // Detect duplicate emails within the file itself (case-insensitive):
        // map each lowercased email -> the first row number it appeared on.
        const firstSeenRowByEmail = new Map();
        dataRows.forEach((row, i) => {
          const emailLower = String(row[emailIdx]).trim().toLowerCase();
          if (!firstSeenRowByEmail.has(emailLower)) {
            firstSeenRowByEmail.set(emailLower, i + 2);
          }
        });

        // Fetch every email already in the users collection so we can catch
        // collisions Firebase Auth's own uniqueness check would miss — e.g. a
        // users doc left behind after its Auth account was deleted, or a doc
        // created by a plain Firestore import that never went through Auth.
        const existingUsersSnapshot = await getDocs(collection(db, "users"));
        const existingEmails = new Set(
          existingUsersSnapshot.docs
            .map((d) => String(d.data().email || "").trim().toLowerCase())
            .filter(Boolean),
        );

        let successful = 0;
        let failed = 0;
        const errors = [];

        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];
          const email = String(row[emailIdx]).trim();
          const emailLower = email.toLowerCase();
          const rowNum = i + 2;
          const firstSeenRow = firstSeenRowByEmail.get(emailLower);
          const firstName = String(row[firstNameIdx] ?? "").trim();
          const lastName = String(row[lastNameIdx] ?? "").trim();
          const phone =
            phoneIdx !== -1 ? String(row[phoneIdx] ?? "").trim() : "";

          const userData = {
            firstName,
            lastName,
            name: joinName(firstName, lastName),
            phone,
            techPrepFlag: true,
            userTypes: {
              staff: false,
              coordinator: false,
              instructor: true,
              admin: false,
              pathwayManager: false,
            },
          };

          try {
            if (!firstName || !lastName) {
              throw new Error("First Name/Last Name required");
            }

            if (firstSeenRow !== rowNum) {
              throw new Error(
                `Duplicate email in file — first appears on row ${firstSeenRow}. Skipped.`,
              );
            }

            if (existingEmails.has(emailLower)) {
              throw new Error(
                "A user with this email already exists in the system. Skipped.",
              );
            }

            const result = await createTpInstructorUser(
              email,
              DEFAULT_PASSWORD,
              userData,
            );

            if (!result.success) {
              throw new Error(result.message || "Unknown error");
            }
            successful++;
            // Prevent a later duplicate row in the same file from also
            // passing the existing-email check once this one succeeds.
            existingEmails.add(emailLower);
          } catch (err) {
            failed++;
            errors.push(`Row ${rowNum}: ${email} — ${err.message}`);
          }

          setProcessed(i + 1);
          setResults({ successful, failed, errors: [...errors] });
        }
      } catch (err) {
        setParseError(`Error reading file: ${err.message}`);
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      setParseError("Error reading file.");
      setUploading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const done = total > 0 && processed === total && !uploading;

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
      >
        Bulk Upload Instructors
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Bulk Upload Instructors
        </ModalDialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download the template below, fill in one row per instructor, then
            upload it here. The file must include <strong>Email</strong>,{" "}
            <strong>First Name</strong>, and <strong>Last Name</strong>{" "}
            columns; <strong>Phone</strong> is optional. Every account
            created will be flagged as a Tech Prep Instructor, with a default
            password of <strong>{DEFAULT_PASSWORD}</strong>. Rows with a
            duplicate email (within the file or already in the system) are
            skipped and reported below rather than creating a duplicate
            account.
          </Typography>

          <Button
            variant="outlined"
            color="success"
            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
            onClick={handleDownloadTemplate}
            sx={{ mb: 2 }}
          >
            Download Template
          </Button>

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {!uploading && total === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                disabled={!file}
                onClick={handleUpload}
              >
                Start Upload
              </Button>
            </Box>
          )}

          {(uploading || total > 0) && (
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  {done ? "Upload complete" : "Creating instructor accounts…"}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {processed} of {total} ({progressPct}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={done && results.failed > 0 ? "warning" : "primary"}
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                <Typography variant="body2" color="success.main">
                  Successful: {results.successful}
                </Typography>
                <Typography variant="body2" color="error.main">
                  Failed: {results.failed}
                </Typography>
              </Box>

              {results.errors.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {results.errors.map((err, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      color="error.main"
                      component="div"
                    >
                      {err}
                    </Typography>
                  ))}
                </Box>
              )}

              {done && (
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={handleClose}
                >
                  Done
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstructorBulkUpload;
